// ============================================================================
// Client Registry — real FastAPI + MongoDB backend (Phase 2 · Step 1)
// ----------------------------------------------------------------------------
// Source of truth = backend `/api/client-registry`. To keep the large existing
// (synchronous) consumer surface working unchanged, we hold an in-memory cache
// hydrated once at auth bootstrap. Sync getters read the cache; mutations update
// the cache optimistically and persist to the backend in the background,
// re-hydrating on error to reconcile with server truth.
// ============================================================================

import { api } from "@/lib/api";

// Piani sottoscrizione + colori.
export const PLANS = {
  starter: { label: "Starter", classes: "bg-emerald-500/10 text-emerald-500 border-emerald-500/20", dot: "bg-emerald-500" },
  professional: { label: "Professional", classes: "bg-blue-500/10 text-blue-500 border-blue-500/20", dot: "bg-blue-500" },
  enterprise: { label: "Enterprise", classes: "bg-sky-400/10 text-sky-400 border-sky-400/20", dot: "bg-sky-400" },
  essential: { label: "Essential", classes: "bg-orange-500/10 text-orange-500 border-orange-500/20", dot: "bg-orange-500" },
  custom: { label: "Custom", classes: "bg-fuchsia-500/10 text-fuchsia-500 border-fuchsia-500/20", dot: "bg-fuchsia-500" },
};
export const PLAN_ORDER = ["starter", "professional", "enterprise", "essential"];
export const PAYMENT_PREFERENCES = ["SDD (addebito diretto)", "Bonifico bancario", "Carta di credito"];
export const CLIENT_STATUS = { active: "Attivo", inactive: "Inattivo" };

export const randomPassword = () =>
  Math.random().toString(36).slice(2, 6) + Math.random().toString(36).slice(2, 6).toUpperCase();

// ---------------------------------------------------------------- cache layer
let _cache = [];
let _hydrated = false;
const _subs = new Set();
const notify = () => _subs.forEach((fn) => { try { fn(); } catch { /* noop */ } });

export const subscribeRegistry = (fn) => { _subs.add(fn); return () => _subs.delete(fn); };
export const isRegistryReady = () => _hydrated;

export async function hydrateRegistry() {
  try {
    const { data } = await api.get("/client-registry");
    _cache = Array.isArray(data) ? data : [];
  } catch (e) {
    // leave whatever we had; consumers degrade gracefully
    // eslint-disable-next-line no-console
    console.error("hydrateRegistry failed", e?.response?.status);
  } finally {
    _hydrated = true;
    notify();
  }
  return _cache;
}

export const resetRegistry = () => { _cache = []; _hydrated = false; };

const idx = (id) => _cache.findIndex((c) => c.id === id);
const replace = (id, next) => { const i = idx(id); if (i >= 0) { _cache[i] = next; } };
const resync = () => hydrateRegistry();

// ---------------------------------------------------------------- sync getters
export const getRegistryClients = () => _cache;
export const getActiveRegistryClients = () => _cache.filter((c) => c.status === "active");
export const getRegistryClient = (id) => _cache.find((c) => c.id === id) || null;

// Nome da mostrare ovunque: RAGIONE SOCIALE (company_name) con fallback.
export const clientDisplayName = (c) => (c && (c.company_name || c.client_name || c.name || c.id)) || "";
// Riga brand da mostrare sotto la ragione sociale ("Brand A · Brand B"), o null.
export const clientBrandLine = (c) => {
  const b = (c && c.brands) || [];
  return b.length ? b.join(" · ") : null;
};
// Risolve nome+brand dalla cache anagrafica tramite id (fonte di verità unica).
export const clientLabelById = (id) => {
  const c = getRegistryClient(id);
  return { name: clientDisplayName(c) || id, brandLine: clientBrandLine(c) };
};

// Opzioni per i selettori cliente: attivi PRIMA, poi i non attivi (comunque selezionabili).
// Ordinati alfabeticamente dentro ciascun gruppo. Badge di stato per i non attivi.
// Nota: `status` è sempre "active" nel backend; lo stato reale è in `status_label`.
export const isClientActive = (c) =>
  (c.status_label || (c.status === "active" ? "Attivo" : "Terminato")) === "Attivo";

export const clientSelectOptions = (clients) => {
  const list = clients || _cache;
  const name = (c) => c.company_name || c.client_name || c.id;
  const byName = (a, b) => name(a).localeCompare(name(b), "it", { sensitivity: "base" });
  const mk = (c) => ({
    value: c.id,
    label: name(c),
    badge: isClientActive(c) ? "" : (c.status_label || "Terminato"),
  });
  const active = list.filter(isClientActive).sort(byName).map(mk);
  const others = list.filter((c) => !isClientActive(c)).sort(byName).map(mk);
  return [...active, ...others];
};

// ---------------------------------------------------------------- clients CRUD
export const addRegistryClient = (data) => {
  const id = `c_new_${Date.now()}`;
  const optimistic = {
    id, status: "active", plan: "starter", team: [], country: "Italia",
    portal_enabled: false, portal_email: "", portal_password: "", force_password_change: false,
    notes: "", secondary_contacts: [], mapping_sections: [], ...data,
  };
  _cache = [..._cache, optimistic];
  notify();
  api.post("/client-registry", { id, ...data })
    .then(({ data: full }) => { replace(id, full); notify(); })
    .catch(resync);
  return id;
};

export const updateRegistryClient = (id, patch) => {
  const i = idx(id);
  if (i >= 0) { _cache[i] = { ..._cache[i], ...patch }; notify(); }
  api.patch(`/client-registry/${id}`, patch)
    .then(({ data }) => { const j = idx(id); if (j >= 0 && data) { _cache[j] = { ..._cache[j], ...data }; notify(); } })
    .catch(resync);
};

export const softDeleteRegistryClient = (id) => {
  _cache = _cache.filter((c) => c.id !== id);
  notify();
  return api.delete(`/client-registry/${id}`).catch((e) => { resync(); throw e; });
};

export const CLIENT_STATUS_OPTIONS = ["Attivo", "Senza contratto", "Terminato", "Bloccato"];
export const setClientStatus = async (id, status, reason) => {
  const r = await api.patch(`/client-registry/${id}/status`, { status, reason });
  await resync();
  return r.data;
};
export const restoreClientAutoStatus = async (id) => {
  const r = await api.post(`/client-registry/${id}/status/auto`);
  await resync();
  return r.data;
};
export const getClientDependencies = (id) => api.get(`/client-registry/${id}/dependencies`).then((r) => r.data);

// ---------------------------------------------------------------- secondary contacts
export const listSecondaryContacts = (id) => getRegistryClient(id)?.secondary_contacts || [];

export const addSecondaryContact = (id, c) => {
  const tmp = `sc_tmp_${Date.now()}`;
  const contact = { id: tmp, job_title: c.job_title || "", first_name: c.first_name || "", last_name: c.last_name || "", email: c.email || "", phone: c.phone || "" };
  const i = idx(id);
  if (i < 0) return;
  _cache[i] = { ..._cache[i], secondary_contacts: [...(_cache[i].secondary_contacts || []), contact] };
  notify();
  api.post(`/client-registry/${id}/contacts`, c)
    .then(({ data }) => {
      const j = idx(id);
      if (j >= 0) { _cache[j] = { ..._cache[j], secondary_contacts: _cache[j].secondary_contacts.map((x) => (x.id === tmp ? { ...x, id: data.id } : x)) }; notify(); }
    })
    .catch(resync);
};

export const updateSecondaryContact = (id, scId, patch) => {
  const i = idx(id);
  if (i < 0) return;
  _cache[i] = { ..._cache[i], secondary_contacts: (_cache[i].secondary_contacts || []).map((sc) => (sc.id === scId ? { ...sc, ...patch } : sc)) };
  notify();
  api.patch(`/client-registry/${id}/contacts/${scId}`, patch).catch(resync);
};

export const removeSecondaryContact = (id, scId) => {
  const i = idx(id);
  if (i < 0) return;
  _cache[i] = { ..._cache[i], secondary_contacts: (_cache[i].secondary_contacts || []).filter((sc) => sc.id !== scId) };
  notify();
  api.delete(`/client-registry/${id}/contacts/${scId}`).catch(resync);
};

// ---------------------------------------------------------------- mapping sections
export const listMappingSections = (id) => getRegistryClient(id)?.mapping_sections || [];

export const addMappingSection = (id, title) => {
  const tmp = `map_tmp_${Date.now()}`;
  const sec = { id: tmp, title: title || "Nuova sezione", content: "" };
  const i = idx(id);
  if (i < 0) return;
  _cache[i] = { ..._cache[i], mapping_sections: [...(_cache[i].mapping_sections || []), sec] };
  notify();
  api.post(`/client-registry/${id}/mapping`, { title: sec.title })
    .then(({ data }) => {
      const j = idx(id);
      if (j >= 0) { _cache[j] = { ..._cache[j], mapping_sections: _cache[j].mapping_sections.map((s) => (s.id === tmp ? { ...s, id: data.id } : s)) }; notify(); }
    })
    .catch(resync);
};

export const updateMappingSection = (id, secId, patch) => {
  const i = idx(id);
  if (i < 0) return;
  _cache[i] = { ..._cache[i], mapping_sections: (_cache[i].mapping_sections || []).map((s) => (s.id === secId ? { ...s, ...patch } : s)) };
  notify();
  api.patch(`/client-registry/${id}/mapping/${secId}`, patch).catch(resync);
};

export const removeMappingSection = (id, secId) => {
  const i = idx(id);
  if (i < 0) return;
  _cache[i] = { ..._cache[i], mapping_sections: (_cache[i].mapping_sections || []).filter((s) => s.id !== secId) };
  notify();
  api.delete(`/client-registry/${id}/mapping/${secId}`).catch(resync);
};

// --- Template di mappatura (globali, riutilizzabili) ---
export const listMappingTemplates = () => api.get("/client-registry/mapping-templates/list").then((r) => r.data);
export const createMappingTemplate = (name, sections) =>
  api.post("/client-registry/mapping-templates/create", { name, sections }).then((r) => r.data);
export const deleteMappingTemplate = (tid) =>
  api.delete(`/client-registry/mapping-templates/${tid}`).then((r) => r.data);
export const applyMappingTemplate = async (clientId, templateId) => {
  const r = await api.post(`/client-registry/${clientId}/mapping/apply-template`, { template_id: templateId });
  await resync();
  return r.data;
};
